import React, { useEffect, useState } from 'react';
import { ArrowRight, Factory, Shield, TrendingDown, TrendingUp, Users, Award, Target, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { productsAPI } from '../services/api';
import { mockCategories, mockTestimonials } from '../mock';
import Header from '../components/Header';
import Footer from '../components/Footer';
import LoginModal from '../components/LoginModal';
import axios from 'axios';

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [defaultLoginRole, setDefaultLoginRole] = useState('consumer');

  useEffect(() => {
    fetchFeaturedProducts();
    fetchCategories();
  }, []);

  const fetchFeaturedProducts = async () => {
    try {
      const response = await productsAPI.getAll();
      setFeaturedProducts(response.data.slice(0, 4));
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Fallback to mock categories if API fails
      setCategories(mockCategories);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-gray-50 to-white py-12 md:py-20">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div className="space-y-4 md:space-y-6">
              <div className="inline-block">
                <span className="bg-red-100 text-red-600 px-3 md:px-4 py-1.5 md:py-2 rounded-full text-xs md:text-sm font-semibold">
                  Factory-to-Consumer Revolution
                </span>
              </div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Exceptional Products.
                <span className="text-red-600"> For You. By You.</span>
              </h1>
              <p className="text-base md:text-xl text-gray-600">
                Experience absolute transparency in pricing. Quality products at near-production costs. No middlemen, just direct value.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
                <Button size="lg" className="bg-red-subtle-stripes hover:bg-red-700 w-full sm:w-auto" asChild>
                  <Link to="/products">
                    Explore Products <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" className="w-full sm:w-auto" asChild>
                  <a href="#how-it-works">How It Works</a>
                </Button>
              </div>
              <div className="flex items-center justify-between sm:justify-start sm:space-x-8 pt-4">
                <div>
                  <div className="text-2xl md:text-3xl font-bold text-gray-900">72%</div>
                  <div className="text-xs md:text-sm text-gray-600">Cost Savings</div>
                </div>
                <div>
                  <div className="text-2xl md:text-3xl font-bold text-gray-900">100%</div>
                  <div className="text-xs md:text-sm text-gray-600">Transparency</div>
                </div>
                <div>
                  <div className="text-2xl md:text-3xl font-bold text-gray-900">Lab</div>
                  <div className="text-xs md:text-sm text-gray-600">Tested Quality</div>
                </div>
              </div>
            </div>
            <div className="relative hidden md:block">
              <img
                src="https://images.unsplash.com/photo-1647427060118-4911c9821b82?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBmYWN0b3J5fGVufDB8fHx8MTc2MTQ0MDE4MXww&ixlib=rb-4.1.0&q=85"
                alt="Modern Factory"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* 4 Values Section */}
      <section className="py-12 md:py-16 bg-gray-900 text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-2xl md:text-3xl font-bold mb-3 md:mb-4">Our Unflinching Values</h2>
            <p className="text-sm md:text-base text-gray-400">Represented by the 4 stripes in our logo</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            <div className="text-center group">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-red-subtle-stripes rounded-lg mx-auto mb-3 md:mb-4 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Shield className="h-6 w-6 md:h-8 md:w-8 text-white" />
              </div>
              <h3 className="text-base md:text-xl font-bold mb-1 md:mb-2">Transparency</h3>
              <p className="text-gray-400 text-xs md:text-sm">Complete cost breakdown for every product</p>
            </div>
            <div className="text-center group">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-red-subtle-stripes rounded-lg mx-auto mb-3 md:mb-4 flex items-center justify-center group-hover:scale-110 transition-transform">
                <TrendingDown className="h-6 w-6 md:h-8 md:w-8 text-white" />
              </div>
              <h3 className="text-base md:text-xl font-bold mb-1 md:mb-2">Cost</h3>
              <p className="text-gray-400 text-xs md:text-sm">Near-production pricing eliminating markups</p>
            </div>
            <div className="text-center group">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-red-subtle-stripes rounded-lg mx-auto mb-3 md:mb-4 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Award className="h-6 w-6 md:h-8 md:w-8 text-white" />
              </div>
              <h3 className="text-base md:text-xl font-bold mb-1 md:mb-2">Quality</h3>
              <p className="text-gray-400 text-xs md:text-sm">Lab-tested products with warranty</p>
            </div>
            <div className="text-center group">
              <div className="w-12 h-12 md:w-16 md:h-16 bg-red-subtle-stripes rounded-lg mx-auto mb-3 md:mb-4 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Users className="h-6 w-6 md:h-8 md:w-8 text-white" />
              </div>
              <h3 className="text-base md:text-xl font-bold mb-1 md:mb-2">Service</h3>
              <p className="text-gray-400 text-xs md:text-sm">Customer-first support and maximum warranty</p>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-12 md:py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl md:text-4xl font-bold text-center mb-8 md:mb-12">Shop by Category</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {categories.length > 0 ? categories.map((category) => (
              <Link key={category.id} to={`/products?category=${category.name}`}>
                <Card className="group overflow-hidden cursor-pointer hover:shadow-xl transition-all">
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={category.image}
                      alt={category.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-3 md:p-4">
                    <h3 className="font-bold text-sm md:text-lg mb-1">{category.name}</h3>
                    <p className="text-xs md:text-sm text-gray-600">{category.description}</p>
                  </div>
                </Card>
              </Link>
            )) : (
              <div className="col-span-full text-center py-8 text-gray-500">
                No categories available yet. Admin can add categories from admin panel.
              </div>
            )}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-12 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl md:text-4xl font-bold mb-3 md:mb-4">How F2C Works</h2>
            <p className="text-base md:text-xl text-gray-600">From factory floor to your door</p>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 md:gap-8 relative">
            <div className="relative">
              <div className="bg-white p-6 md:p-8 rounded-xl shadow-lg">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-red-600 text-white rounded-lg flex items-center justify-center text-xl md:text-2xl font-bold mb-3 md:mb-4">
                  1
                </div>
                <h3 className="text-lg md:text-xl font-bold mb-2 md:mb-3">Direct Sourcing</h3>
                <p className="text-sm md:text-base text-gray-600">
                  We connect directly with manufacturers who create quality products. No distributors, no marketplace margins.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="bg-white p-6 md:p-8 rounded-xl shadow-lg">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-red-600 text-white rounded-lg flex items-center justify-center text-xl md:text-2xl font-bold mb-3 md:mb-4">
                  2
                </div>
                <h3 className="text-lg md:text-xl font-bold mb-2 md:mb-3">Quality Testing</h3>
                <p className="text-sm md:text-base text-gray-600">
                  Every product undergoes rigorous testing by world-class labs. Only the best make it to our platform.
                </p>
              </div>
            </div>
            <div className="relative sm:col-span-2 md:col-span-1">
              <div className="bg-white p-6 md:p-8 rounded-xl shadow-lg">
                <div className="w-12 h-12 bg-red-600 text-white rounded-lg flex items-center justify-center text-2xl font-bold mb-4">
                  3
                </div>
                <h3 className="text-xl font-bold mb-3">To You</h3>
                <p className="text-gray-600">
                  Products reach you at near-production costs with full transparency on pricing breakdown.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cost Transparency Section */}
      <section id="transparency" className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Complete Cost Transparency</h2>
            <p className="text-xl text-gray-600">See exactly where your money goes</p>
          </div>
          <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2">
                    <th className="text-left py-4 px-4">Cost Component</th>
                    <th className="text-right py-4 px-4">Traditional Retail</th>
                    <th className="text-right py-4 px-4">Online Marketplace</th>
                    <th className="text-right py-4 px-4 text-red-600">Youseful</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="py-4 px-4">Manufacturing</td>
                    <td className="text-right py-4 px-4">₹1,200</td>
                    <td className="text-right py-4 px-4">₹1,200</td>
                    <td className="text-right py-4 px-4">₹1,200</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-4 px-4">Distribution Layers</td>
                    <td className="text-right py-4 px-4">₹2,400</td>
                    <td className="text-right py-4 px-4">₹1,200</td>
                    <td className="text-right py-4 px-4 text-green-600">₹0</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-4 px-4">Marketing & Overhead</td>
                    <td className="text-right py-4 px-4">₹3,600</td>
                    <td className="text-right py-4 px-4">₹2,400</td>
                    <td className="text-right py-4 px-4 text-green-600">₹300</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-4 px-4">Platform/Retail Margin</td>
                    <td className="text-right py-4 px-4">₹1,800</td>
                    <td className="text-right py-4 px-4">₹1,200</td>
                    <td className="text-right py-4 px-4 text-green-600">₹200</td>
                  </tr>
                  <tr className="font-bold text-lg">
                    <td className="py-4 px-4">Final Price (Pre-GST)</td>
                    <td className="text-right py-4 px-4">₹9,000</td>
                    <td className="text-right py-4 px-4">₹6,000</td>
                    <td className="text-right py-4 px-4 text-red-600">₹1,700</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">Example based on a ₹1,200 manufacturing cost product</p>
            </div>
          </div>
        </div>
      </section>

      {/* Designer Collaboration Section */}
      <section id="designers" className="py-12 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div className="order-2 md:order-1">
              <img
                src="https://images.unsplash.com/photo-1604952703578-8ae924053711?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxwcm9kdWN0JTIwZGVzaWduZXIlMjBza2V0Y2hpbmd8ZW58MHx8fHwxNzYxNDY2MjAzfDA&ixlib=rb-4.1.0&q=85"
                alt="Designer Collaboration"
                className="rounded-2xl shadow-xl w-full"
              />
            </div>
            <div className="space-y-4 md:space-y-6 order-1 md:order-2">
              <h2 className="text-2xl md:text-4xl font-bold">For Designers, By Designers</h2>
              <p className="text-base md:text-xl text-gray-600">
                We collaborate with talented designers, credit their work, and share lifetime royalties. Every product tells the story of its creator.
              </p>
              <div className="space-y-3 md:space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <Award className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-sm md:text-base">Full Credit</h4>
                    <p className="text-gray-600 text-sm md:text-base">Your name on every product you design</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <TrendingDown className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-sm md:text-base">Lifetime Royalties</h4>
                    <p className="text-gray-600 text-sm md:text-base">Earn on every sale, forever</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <Target className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-sm md:text-base">Platform Support</h4>
                    <p className="text-gray-600 text-sm md:text-base">We handle production, quality, and delivery</p>
                  </div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  size="lg" 
                  className="bg-red-subtle-stripes hover:bg-red-700 w-full sm:w-auto"
                  onClick={() => {
                    setDefaultLoginRole('designer');
                    setShowLoginModal(true);
                  }}
                >
                  Designer Login
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="w-full sm:w-auto"
                  onClick={() => window.location.href = '/role-selection'}
                >
                  Register as Designer
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Micro Investors Section */}
      <section className="py-12 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            <div>
              <h2 className="text-2xl md:text-4xl font-bold mb-4 md:mb-6">Become a Micro Investor</h2>
              <p className="text-base md:text-xl text-gray-600 mb-6 md:mb-8">
                Invest in products you believe in. Track sales in real-time. Earn minimum 12% annual returns with all upside for investors.
              </p>
              <div className="grid grid-cols-1 gap-4 md:gap-6 mb-6 md:mb-8">
                <Card className="p-4 md:p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start gap-3">
                    <Clock className="h-8 w-8 md:h-10 md:w-10 text-red-600 flex-shrink-0" />
                    <div>
                      <h3 className="font-bold text-base md:text-lg mb-1">Real-Time Tracking</h3>
                      <p className="text-xs md:text-sm text-gray-600">Live sales dashboard for your invested products</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-4 md:p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start gap-3">
                    <Factory className="h-8 w-8 md:h-10 md:w-10 text-red-600 flex-shrink-0" />
                    <div>
                      <h3 className="font-bold text-base md:text-lg mb-1">Direct Investment</h3>
                      <p className="text-xs md:text-sm text-gray-600">Fund inventory and be part of the supply chain</p>
                    </div>
                  </div>
                </Card>
                <Card className="p-4 md:p-6 hover:shadow-lg transition-shadow">
                  <div className="flex items-start gap-3">
                    <TrendingUp className="h-8 w-8 md:h-10 md:w-10 text-red-600 flex-shrink-0" />
                    <div>
                      <h3 className="font-bold text-base md:text-lg mb-1">Fair Returns</h3>
                      <p className="text-xs md:text-sm text-gray-600">Minimum 12% annual returns with all upside for investors</p>
                    </div>
                  </div>
                </Card>
              </div>
            
            <div className="flex flex-col sm:flex-row gap-3 mt-6 md:mt-8">
              <Button 
                size="lg" 
                className="bg-red-600 hover:bg-red-700 w-full sm:w-auto"
                onClick={() => {
                  setDefaultLoginRole('investor');
                  setShowLoginModal(true);
                }}
              >
                Investor Login
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="w-full sm:w-auto"
                onClick={() => window.location.href = '/role-selection'}
              >
                Register as Investor
              </Button>
            </div>
            </div>
            
            <div className="hidden md:block">
              <img
                src="https://images.pexels.com/photos/6779716/pexels-photo-6779716.jpeg"
                alt="Investment Analytics"
                className="rounded-2xl shadow-xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12">Featured Products</h2>
          <div className="grid md:grid-cols-4 gap-6">
            {featuredProducts.map((product) => (
              <Link key={product.id} to={`/products/${product.id}`}>
                <Card className="group overflow-hidden hover:shadow-xl transition-shadow">
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-4">
                    <div className="text-sm text-gray-500 mb-1">Designed by {product.designer}</div>
                    <h3 className="font-bold mb-2">{product.name}</h3>
                    <div className="flex items-baseline gap-2 mb-1">
                      <div className="text-2xl font-bold text-gray-900">₹{product.price.toLocaleString()}</div>
                      <div className="text-sm text-gray-500">Our Price</div>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <div className="text-gray-500">
                        Market: <span className="line-through">₹{product.originalPrice.toLocaleString()}</span>
                      </div>
                      <div className="text-green-600 font-semibold">
                        {Math.round((1 - product.price / product.originalPrice) * 100)}% Less
                      </div>
                    </div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
          <div className="text-center mt-8">
            <Button size="lg" variant="outline" asChild>
              <Link to="/products">View All Products</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12">What People Say</h2>
          
          {/* Text Testimonials */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {mockTestimonials.map((testimonial) => (
              <Card key={testimonial.id} className="p-6">
                <div className="flex items-center mb-4">
                  <img
                    src={testimonial.avatar}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full mr-3"
                  />
                  <div>
                    <div className="font-bold">{testimonial.name}</div>
                    <div className="text-sm text-gray-600">{testimonial.role}</div>
                  </div>
                </div>
                <p className="text-gray-700 mb-4">{testimonial.content}</p>
                <div className="flex">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <span key={i} className="text-yellow-500">★</span>
                  ))}
                </div>
              </Card>
            ))}
          </div>

          {/* Video Testimonials */}
          <div className="mt-16">
            <h3 className="text-3xl font-bold text-center mb-8">Video Testimonials</h3>
            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {/* Video Testimonial 1 */}
              <Card className="overflow-hidden">
                <div className="aspect-video bg-gray-900 relative group">
                  <img
                    src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=800&h=450&fit=crop"
                    alt="Customer testimonial"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <button className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center hover:bg-red-700 transition-colors group-hover:scale-110 transition-transform">
                      <svg className="w-8 h-8 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-center mb-2">
                    <img
                      src="https://i.pravatar.cc/150?img=2"
                      alt="Rahul Sharma"
                      className="w-10 h-10 rounded-full mr-3"
                    />
                    <div>
                      <div className="font-bold">Rahul Sharma</div>
                      <div className="text-sm text-gray-600">Early Adopter</div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700">"The pricing transparency changed how I shop online. Knowing exactly where my money goes builds trust."</p>
                </div>
              </Card>

              {/* Video Testimonial 2 */}
              <Card className="overflow-hidden">
                <div className="aspect-video bg-gray-900 relative group">
                  <img
                    src="https://images.unsplash.com/photo-1580489944761-15a19d654956?w=800&h=450&fit=crop"
                    alt="Customer testimonial"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <button className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center hover:bg-red-700 transition-colors group-hover:scale-110 transition-transform">
                      <svg className="w-8 h-8 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-center mb-2">
                    <img
                      src="https://i.pravatar.cc/150?img=9"
                      alt="Priya Menon"
                      className="w-10 h-10 rounded-full mr-3"
                    />
                    <div>
                      <div className="font-bold">Priya Menon</div>
                      <div className="text-sm text-gray-600">Designer Partner</div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700">"As a designer, seeing my work reach customers directly with fair compensation is incredibly rewarding."</p>
                </div>
              </Card>

              {/* Video Testimonial 3 */}
              <Card className="overflow-hidden">
                <div className="aspect-video bg-gray-900 relative group">
                  <img
                    src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=800&h=450&fit=crop"
                    alt="Customer testimonial"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <button className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center hover:bg-red-700 transition-colors group-hover:scale-110 transition-transform">
                      <svg className="w-8 h-8 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-center mb-2">
                    <img
                      src="https://i.pravatar.cc/150?img=12"
                      alt="Arjun Patel"
                      className="w-10 h-10 rounded-full mr-3"
                    />
                    <div>
                      <div className="font-bold">Arjun Patel</div>
                      <div className="text-sm text-gray-600">Micro Investor</div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700">"Investing in products I love and earning returns - it's a win-win model that makes sense."</p>
                </div>
              </Card>

              {/* Video Testimonial 4 */}
              <Card className="overflow-hidden">
                <div className="aspect-video bg-gray-900 relative group">
                  <img
                    src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=800&h=450&fit=crop"
                    alt="Customer testimonial"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                    <button className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center hover:bg-red-700 transition-colors group-hover:scale-110 transition-transform">
                      <svg className="w-8 h-8 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="p-4">
                  <div className="flex items-center mb-2">
                    <img
                      src="https://i.pravatar.cc/150?img=6"
                      alt="Neha Gupta"
                      className="w-10 h-10 rounded-full mr-3"
                    />
                    <div>
                      <div className="font-bold">Neha Gupta</div>
                      <div className="text-sm text-gray-600">Regular Customer</div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-700">"The quality is outstanding and prices are unbeatable. It's my go-to platform for online shopping now."</p>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-12">Frequently Asked Questions</h2>
            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="item-1" className="bg-white px-6 rounded-lg">
                <AccordionTrigger className="text-left font-semibold">
                  How do you ensure product quality?
                </AccordionTrigger>
                <AccordionContent className="text-gray-600">
                  Every product is tested by world-class laboratories before listing. We provide lab reports and offer maximum warranty duration for complete peace of mind.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-2" className="bg-white px-6 rounded-lg">
                <AccordionTrigger className="text-left font-semibold">
                  What makes Youseful different from other e-commerce platforms?
                </AccordionTrigger>
                <AccordionContent className="text-gray-600">
                  We eliminate multiple distribution layers and marketplace margins, offering products at near-production costs with complete pricing transparency. Every cost breakdown is visible on our website.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-3" className="bg-white px-6 rounded-lg">
                <AccordionTrigger className="text-left font-semibold">
                  How can I become a designer or micro investor?
                </AccordionTrigger>
                <AccordionContent className="text-gray-600">
                  Designers can apply through our platform to collaborate and earn lifetime royalties. Micro investors can create an account and choose products to invest in through our seller-type app with live sales tracking.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-4" className="bg-white px-6 rounded-lg">
                <AccordionTrigger className="text-left font-semibold">
                  What is your return and warranty policy?
                </AccordionTrigger>
                <AccordionContent className="text-gray-600">
                  We offer maximum warranty duration for all products and hassle-free returns. Quality and customer satisfaction are our top priorities.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-red-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Experience True Transparency?</h2>
          <p className="text-xl mb-8 opacity-90">
            Join thousands of customers who've discovered quality products at honest prices.
          </p>
          <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100" asChild>
            <Link to="/products">
              Start Shopping Now <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </div>
      </section>

      <Footer />
      
      <LoginModal 
        isOpen={showLoginModal} 
        onClose={() => setShowLoginModal(false)}
        defaultRole={defaultLoginRole}
      />
    </div>
  );
};

export default Home;
